import { useState, useEffect } from 'react';
import { syncNotificationPrefsToServer } from '@/hooks/use-preferences';

const STORAGE_KEY = 'eligaro-notification-prefs';

interface NotificationPrefs {
  emailNotifications: boolean;
  matchNotifications: boolean;
  assignmentAlerts: boolean;
  weeklyDigest: boolean;
}

const defaultPrefs: NotificationPrefs = {
  emailNotifications: true,
  matchNotifications: true,
  assignmentAlerts: true,
  weeklyDigest: false
};

function loadPrefs(): NotificationPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...defaultPrefs };
    return { ...defaultPrefs, ...(JSON.parse(raw) as Partial<NotificationPrefs>) };
  } catch {
    return { ...defaultPrefs };
  }
}

function savePrefs(prefs: NotificationPrefs) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  syncNotificationPrefsToServer(prefs);
}

interface ToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}

function ToggleRow({ label, description, checked, onChange }: ToggleRowProps) {
  return (
    <div className="flex items-center justify-between py-4">
      <div className="flex-1 pr-4">
        <p className="text-sm font-medium">{label}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
      </div>
      <button
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
          checked ? 'bg-primary' : 'bg-muted'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

export function NotificationPreferencesTab() {
  const [prefs, setPrefs] = useState<NotificationPrefs>(loadPrefs);

  useEffect(() => {
    savePrefs(prefs);
  }, [prefs]);

  const updatePref = (key: keyof NotificationPrefs, value: boolean) => {
    setPrefs((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-2">
      <div>
        <h3 className="mb-1 text-sm font-semibold">Notification Preferences</h3>
        <p className="text-sm text-muted-foreground">
          Control which notifications you receive from Eligaro.
        </p>
      </div>

      <div className="divide-y rounded-lg border">
        <div className="px-4">
          <ToggleRow
            label="Email Notifications"
            description="Receive email when new matches are found"
            checked={prefs.emailNotifications}
            onChange={(v) => updatePref('emailNotifications', v)}
          />
        </div>
        <div className="px-4">
          <ToggleRow
            label="Match Notifications"
            description="Get notified about high-scoring matches (score 7 or above)"
            checked={prefs.matchNotifications}
            onChange={(v) => updatePref('matchNotifications', v)}
          />
        </div>
        <div className="px-4">
          <ToggleRow
            label="Assignment Alerts"
            description="Alert when new assignments match your skills"
            checked={prefs.assignmentAlerts}
            onChange={(v) => updatePref('assignmentAlerts', v)}
          />
        </div>
        <div className="px-4">
          <ToggleRow
            label="Weekly Digest"
            description="Receive a weekly summary of matches and activity"
            checked={prefs.weeklyDigest}
            onChange={(v) => updatePref('weeklyDigest', v)}
          />
        </div>
      </div>
    </div>
  );
}
